import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { withStyles } from '@material-ui/core/styles';

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
  fetchSignInMethodsForEmail,
  getMultiFactorResolver,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebaseConfig';

import MFAChallengeDialog from '../Components/MFA/MFAChallengeDialog';

import { GoogleLogin } from '@react-oauth/google';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import getBrowserFingerprint from 'get-browser-fingerprint';

import API from '../Util/api';
import MultiUseDialog from '../Components/MultiUseDialog';
import ProgressIndicator from '../Components/ProgressIndicator';
import { ValidateEmail } from '../Util/Regex';
import { IsMobile } from '../Util/MobileDetector';
import {
  buildMarketingReferrer,
  captureOrReadMarketingAttribution,
  clearMarketingAttribution,
  readMarketingAttribution,
} from '../Util/MarketingAttribution';
import { pushMarketingEvent } from '../Util/MarketingEvents';

const paneAndLinksColor = "#eee";
const styles = theme => ({
  root: {
    width: "100%",
    height: "100%",
    backgroundImage: "url('/bg3.jpg')",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "fixed",
    backgroundSize: "cover",
    overflowY: "auto",
  },
  container: {
    marginTop: 64,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  paper: {
    backgroundColor: paneAndLinksColor,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
  },
  logo: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  gridControls: {
    textAlign: "center",
  },
  button: {
    textTransform: "none",
  },
  gridLinks: {
    color: paneAndLinksColor,
    marginTop: theme.spacing(1),
    justifyContent: "center",
  },
  divider: {
    backgroundColor: "#aaa",
  }
});

const _browserFingerprint = getBrowserFingerprint(true);

// Firebase providers
const appleProvider = new OAuthProvider('apple.com');
const microsoftProvider = new OAuthProvider('microsoft.com');

const Login = ({ classes, theme }) => {
  // State management
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  // Dialog states
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [generalDialogDetails, setGeneralDialogDetails] = useState({ Open: false });

  // Migration flow states
  const [needsPasswordEstablishment, setNeedsPasswordEstablishment] = useState(false);
  const [migrationOTP, setMigrationOTP] = useState('');
  const [migrationPassword, setMigrationPassword] = useState('');
  const [migrationPasswordConfirm, setMigrationPasswordConfirm] = useState('');

  // Loading states
  const [showProgressIndicator, setShowProgressIndicator] = useState(false);
  const [microsoftSigningIn, setMicrosoftSigningIn] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Account linking
  const [accountLinkingContext, setAccountLinkingContext] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);

  // MFA challenge (Firebase resolver-based, for native OAuth sign-ins)
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaEmail, setMfaEmail] = useState('');

  // Server-side TOTP verification (for custom token sign-ins like LinkedIn)
  const [showTOTPDialog, setShowTOTPDialog] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [totpShowRecovery, setTotpShowRecovery] = useState(false);
  const [totpRecoveryCode, setTotpRecoveryCode] = useState('');
  const [totpRecoveryLoading, setTotpRecoveryLoading] = useState(false);
  const [totpRecoverySuccess, setTotpRecoverySuccess] = useState(false);

  // Google button width
  const [googleButtonWidth, setGoogleButtonWidth] = useState(null);
  const buttonGridRef = useRef(null);

  const passwordInputRef = useRef(null);

  const buildMarketingContext = useCallback((style = 'camel') => {
    const marketingAttribution = readMarketingAttribution();
    const referrer = buildMarketingReferrer({
      fallbackReferrer: document.referrer || '',
    });

    if (style === 'pascal') {
      return {
        Referrer: referrer,
        ...(marketingAttribution ? { MarketingAttribution: marketingAttribution } : {}),
      };
    }

    return {
      referrer,
      ...(marketingAttribution ? { marketingAttribution } : {}),
    };
  }, []);

  // Utility functions
  const showGeneralDialog = useCallback((title, bodyText, closeCallback) => {
    setGeneralDialogDetails({
      Open: true,
      Title: title,
      BodyText: bodyText,
      CloseCallback: () => {
        setGeneralDialogDetails({ Open: false });
        if (closeCallback) closeCallback();
      },
    });
  }, []);

  const handleApiError = useCallback(err => {
    setShowProgressIndicator(false);

    let errorMessage = "Please try again.";
    if (err.response && err.response.data) {
      if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.errors && err.response.data.errors.length > 0) {
        errorMessage = err.response.data.errors[0].message;
      }
    } else if (err.message) {
      errorMessage = err.message;
    }

    showGeneralDialog("Sorry, something went wrong...", errorMessage);
  }, [showGeneralDialog]);

  // Map provider IDs to friendly names
  const providerDisplayName = (providerId) => {
    switch (providerId) {
      case 'google.com': return 'Google';
      case 'apple.com': return 'Apple';
      case 'microsoft.com': return 'Microsoft';
      case 'password': return 'email and password';
      default: return providerId;
    }
  };

  // Handle account-exists-with-different-credential error
  const handleAccountExistsError = useCallback(async (error, attemptedProvider) => {
    const errorEmail = error.customData?.email;
    const credential = OAuthProvider.credentialFromError(error);

    if (!errorEmail) {
      showGeneralDialog("Account Linking Required",
        "An account already exists with this email using a different sign-in method. Please sign in with your original method first.");
      return;
    }

    // Store the pending credential for linking after sign-in
    setPendingCredential(credential);

    // Try to find existing providers, but this may return empty due to email enumeration protection
    let providerName = null;
    try {
      const methods = await fetchSignInMethodsForEmail(auth, errorEmail);
      if (methods.length > 0) {
        providerName = providerDisplayName(methods[0]);
      }
    } catch (e) {
      // Ignore - email enumeration protection may block this
    }

    // Build a helpful message listing other available methods
    const allProviders = ['Google', 'Apple', 'Microsoft', 'email and password'];
    const otherProviders = allProviders.filter(p => p !== attemptedProvider);

    const message = providerName
      ? `An account already exists for ${errorEmail} using ${providerName}. Please sign in with ${providerName} first, and your accounts will be automatically linked.`
      : `An account already exists for ${errorEmail} using a different sign-in method. Please sign in using one of your other methods (${otherProviders.join(', ')}), and your accounts will be automatically linked.`;

    showGeneralDialog("Account Linking", message);
  }, [showGeneralDialog]);

  // Mask email for display
  const maskEmail = (email) => {
    if (!email || !email.includes('@')) return email;
    const [local, domain] = email.split('@');
    if (local.length <= 1) return `${local}***@${domain}`;
    return `${local[0]}***@${domain}`;
  };

  // Check if user needs migration
  const checkMigrationStatus = useCallback(async (email) => {
    if (!email || !ValidateEmail(email)) return;

    try {
      const response = await API.post('/user/migrationCheck', { email });
      const status = response.data;

      if (status.NeedsMigration && !status.HasFirebasePassword) {
        setNeedsPasswordEstablishment(true);
      }
    } catch (err) {
      // Non-critical, continue
      console.error('Migration check failed:', err);
    }
  }, []);

  // Effect to check migration when email changes
  useEffect(() => {
    if (email && ValidateEmail(email)) {
      checkMigrationStatus(email);
    }
  }, [email, checkMigrationStatus]);

  // Google button width - resize handler
  useEffect(() => {
    const handleResize = () => {
      if (buttonGridRef.current && buttonGridRef.current.clientWidth) {
        setGoogleButtonWidth(
          buttonGridRef.current.clientWidth - (theme.spacing(1) || 0)
        );
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  useEffect(() => {
    captureOrReadMarketingAttribution({
      href: window.location.href,
      referrer: document.referrer || '',
    });

    pushMarketingEvent({
      event: 'nucleus_login_view',
      auth_mode: 'login_or_signup',
    });
  }, []);

  // Main Firebase login function
  const doFirebaseLogin = useCallback(async (firebaseUser, mfaToken, authProvider = 'firebase') => {
    try {
      const idToken = await firebaseUser.getIdToken();

      const response = await API.post('/user/firebaseSignin', {
        idToken,
        browserFingerprint: _browserFingerprint,
        isMobile: IsMobile(),
        mfaToken: mfaToken || undefined,
        ...buildMarketingContext('camel'),
      }, {
        withCredentials: true,
      });

      if (response.status === 200) {
        const isNewUser = Boolean(response.data.IsNewUser);
        pushMarketingEvent({
          event: isNewUser ? 'sign_up' : 'login',
          method: authProvider,
        });
        // Clear only after the API explicitly confirms durable persistence.
        // Older API revisions returned 200 while silently discarding this
        // object, so treating a generic success as proof loses attribution.
        if (
          response.data.MarketingAttributionPersisted ||
          !readMarketingAttribution()
        ) {
          clearMarketingAttribution();
        }
        const loginSession = response.data;

        // Store user info
        localStorage.setItem('userEmail', firebaseUser.email);
        localStorage.setItem('userName', firebaseUser.displayName || firebaseUser.email);
        localStorage.setItem('userId', loginSession.UserID);

        // Redirect to app
        setRedirectToReferrer(true);
      }
    } catch (err) {
      // Check for server-side MFA verification required (custom token sign-ins)
      if (err.response?.status === 403 && err.response?.data?.error === 'mfa_verification_required') {
        setPendingFirebaseUser(firebaseUser);
        setShowTOTPDialog(true);
        setShowProgressIndicator(false);
        return;
      }
      handleApiError(err);
    }
  }, [buildMarketingContext, handleApiError]);

  // MFA challenge handler
  const handleMFARequired = useCallback((error, attemptedEmail) => {
    setShowProgressIndicator(false);
    setMicrosoftSigningIn(false);
    const resolver = getMultiFactorResolver(auth, error);
    setMfaResolver(resolver);
    setMfaEmail(attemptedEmail || '');
    setShowMFADialog(true);
  }, []);

  // Email+Password Authentication
  const handleEmailPasswordSubmit = useCallback(async () => {
    if (!email || !ValidateEmail(email)) {
      showGeneralDialog("Invalid Email", "Please enter a valid email address.");
      return;
    }

    // Check if user needs migration
    if (needsPasswordEstablishment) {
      pushMarketingEvent({
        event: 'form_start',
        form_name: 'nucleus_login',
        auth_provider: isLogin ? 'email' : 'email_signup',
      });
      setShowMigrationDialog(true);
      return;
    }

    pushMarketingEvent({
      event: 'form_start',
      form_name: 'nucleus_login',
      auth_provider: isLogin ? 'email' : 'email_signup',
    });
    setShowPasswordDialog(true);
  }, [email, isLogin, needsPasswordEstablishment, showGeneralDialog]);

  const handlePasswordAuth = useCallback(async () => {
    if (!password) {
      showGeneralDialog("Password Required", "Please enter a password.");
      return;
    }

    setShowProgressIndicator(true);
    setShowPasswordDialog(false);

    try {
      let userCredential;

      if (isLogin) {
        // Sign in existing user
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Create new account
        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
          if (error.code === 'auth/email-already-in-use') {
            // Set up linking and tell user what to do
            setAccountLinkingContext({ email, password });
            showGeneralDialog(
              "Account Exists",
              "An account with this email already exists. Please sign in with your existing method (Google, Apple, or Microsoft) and your password will be linked automatically."
            );
            setShowProgressIndicator(false);
            return;
          }
          throw error;
        }
      }

      await doFirebaseLogin(userCredential.user, undefined, isLogin ? 'email' : 'email_signup');
    } catch (error) {
      setShowProgressIndicator(false);
      setPassword('');

      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, email);
      } else if (error.code === 'auth/user-not-found') {
        showGeneralDialog("No Account", "No account exists with this email. Please create an account first.");
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        showGeneralDialog("Incorrect Password", "The password you entered is incorrect. Please try again.");
      } else {
        handleApiError(error);
      }
    }
  }, [email, password, isLogin, doFirebaseLogin, handleApiError, handleMFARequired, showGeneralDialog]);

  // Link pending credential after successful sign-in
  const linkPendingCredentialIfNeeded = useCallback(async (user) => {
    if (pendingCredential) {
      try {
        await linkWithCredential(user, pendingCredential);
        setPendingCredential(null);
      } catch (linkError) {
        console.error('Account linking failed:', linkError);
        // Non-critical - continue with sign-in even if linking fails
      }
    }
    if (accountLinkingContext) {
      try {
        const credential = EmailAuthProvider.credential(
          accountLinkingContext.email,
          accountLinkingContext.password
        );
        await linkWithCredential(user, credential);
        setAccountLinkingContext(null);
      } catch (linkError) {
        console.error('Password linking failed:', linkError);
      }
    }
  }, [pendingCredential, accountLinkingContext]);

  const handleMFASuccess = useCallback(async (userCredential) => {
    setShowMFADialog(false);
    setMfaResolver(null);
    setMfaEmail('');
    setShowProgressIndicator(true);
    await linkPendingCredentialIfNeeded(userCredential.user);
    await doFirebaseLogin(userCredential.user);
  }, [doFirebaseLogin, linkPendingCredentialIfNeeded]);

  const handleMFACancel = useCallback(() => {
    setShowMFADialog(false);
    setMfaResolver(null);
    setMfaEmail('');
  }, []);

  // Server-side TOTP verification (for LinkedIn/custom token sign-ins)
  const handleTOTPVerify = useCallback(async () => {
    if (totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code.');
      return;
    }

    setTotpLoading(true);
    setTotpError('');

    try {
      const idToken = await pendingFirebaseUser.getIdToken();
      const response = await API.post('/user/mfa/verify-totp', {
        idToken,
        totpCode,
      });

      // TOTP verified — retry login with the verification token
      setShowTOTPDialog(false);
      setTotpCode('');
      setShowProgressIndicator(true);
      await doFirebaseLogin(pendingFirebaseUser, response.data.verificationToken, 'totp');
      setPendingFirebaseUser(null);
    } catch (err) {
      console.error('TOTP verification error:', err);
      if (err.response?.status === 401) {
        setTotpError('Invalid code. Please check your authenticator app and try again.');
      } else {
        setTotpError('Verification failed. Please try again.');
      }
    } finally {
      setTotpLoading(false);
    }
  }, [totpCode, pendingFirebaseUser, doFirebaseLogin]);

  const handleTOTPCancel = useCallback(() => {
    setShowTOTPDialog(false);
    setTotpCode('');
    setTotpError('');
    setPendingFirebaseUser(null);
    setTotpShowRecovery(false);
    setTotpRecoveryCode('');
    setTotpRecoverySuccess(false);
  }, []);

  const handleTOTPRecoveryVerify = useCallback(async () => {
    if (!totpRecoveryCode.trim()) {
      setTotpError('Please enter a recovery code.');
      return;
    }

    setTotpRecoveryLoading(true);
    setTotpError('');

    try {
      await API.post('/user/mfa-reset/recovery', {
        email: pendingFirebaseUser?.email || '',
        recoveryCode: totpRecoveryCode.trim(),
      });
      setTotpRecoverySuccess(true);
    } catch (err) {
      console.error('Error verifying recovery code:', err);
      if (err.response?.status === 400) {
        setTotpError('Invalid recovery code. Please try again.');
      } else {
        setTotpError('Something went wrong. Please try again.');
      }
    } finally {
      setTotpRecoveryLoading(false);
    }
  }, [totpRecoveryCode, pendingFirebaseUser]);

  // Google Sign-In (via Google Identity Services button → Firebase credential)
  const handleGoogleLoginResponse = useCallback(async (resp) => {
    if (!resp || !resp.credential) return;

    setShowProgressIndicator(true);
    pushMarketingEvent({
      event: 'form_start',
      form_name: 'nucleus_login',
      auth_provider: 'google',
    });

    try {
      const credential = GoogleAuthProvider.credential(resp.credential);
      const result = await signInWithCredential(auth, credential);
      await linkPendingCredentialIfNeeded(result.user);
      await doFirebaseLogin(result.user, undefined, 'google');
    } catch (error) {
      setShowProgressIndicator(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExistsError(error, 'Google');
      } else {
        handleApiError(error);
      }
    }
  }, [doFirebaseLogin, handleApiError, handleAccountExistsError, handleMFARequired, linkPendingCredentialIfNeeded]);

  // Apple Sign-In
  const handleAppleSignIn = useCallback(async () => {
    setShowProgressIndicator(true);
    pushMarketingEvent({
      event: 'form_start',
      form_name: 'nucleus_login',
      auth_provider: 'apple',
    });

    try {
      const result = await signInWithPopup(auth, appleProvider);
      await linkPendingCredentialIfNeeded(result.user);
      await doFirebaseLogin(result.user, undefined, 'apple');
    } catch (error) {
      setShowProgressIndicator(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExistsError(error, 'Apple');
      } else {
        handleApiError(error);
      }
    }
  }, [doFirebaseLogin, handleApiError, handleAccountExistsError, handleMFARequired, linkPendingCredentialIfNeeded]);

  // Microsoft Sign-In
  const handleMicrosoftSignIn = useCallback(async () => {
    setShowProgressIndicator(true);
    setMicrosoftSigningIn(true);
    pushMarketingEvent({
      event: 'form_start',
      form_name: 'nucleus_login',
      auth_provider: 'microsoft',
    });

    try {
      const result = await signInWithPopup(auth, microsoftProvider);
      await linkPendingCredentialIfNeeded(result.user);
      await doFirebaseLogin(result.user, undefined, 'microsoft');
    } catch (error) {
      setShowProgressIndicator(false);
      setMicrosoftSigningIn(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExistsError(error, 'Microsoft');
      } else {
        handleApiError(error);
      }
    }
  }, [doFirebaseLogin, handleApiError, handleAccountExistsError, handleMFARequired, linkPendingCredentialIfNeeded]);

  // LinkedIn Custom Authentication
  const handleLinkedInSuccess = useCallback(async (code) => {
    setShowProgressIndicator(true);
    pushMarketingEvent({
      event: 'form_start',
      form_name: 'nucleus_login',
      auth_provider: 'linkedin',
    });

    try {
      // Exchange LinkedIn auth code for custom Firebase token
      const response = await API.post('/user/linkedInAuth', {
        linkedInAuthCode: code,
        redirectUri: `${window.location.origin}/login/linkedin`,
        browserFingerprint: _browserFingerprint,
        isMobile: IsMobile(),
        ...buildMarketingContext('camel'),
      });

      const { customToken } = response.data;

      // Sign in to Firebase with custom token
      const userCredential = await signInWithCustomToken(auth, customToken);
      await doFirebaseLogin(userCredential.user, undefined, 'linkedin');
    } catch (error) {
      setShowProgressIndicator(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else {
        handleApiError(error);
      }
    }
  }, [buildMarketingContext, doFirebaseLogin, handleApiError, handleMFARequired]);

  const handleLinkedInFailure = useCallback((error) => {
    console.error('LinkedIn auth failed:', error);
    handleApiError(error);
  }, [handleApiError]);

  // Migration Flow
  const handleStartMigration = useCallback(async () => {
    setShowProgressIndicator(true);

    try {
      // Send OTP
      await API.post('/user/emailLoginOTPSend', {
        UserProvider: 'email',
        Email: email,
        AuthType: 'email',
        BrowserFingerprint: _browserFingerprint,
        IsMobile: IsMobile(),
        ...buildMarketingContext('pascal'),
      });

      setShowMigrationDialog(false);
      setShowOTPDialog(true);
      setShowProgressIndicator(false);
    } catch (err) {
      setShowProgressIndicator(false);
      handleApiError(err);
    }
  }, [buildMarketingContext, email, handleApiError]);

  const handleVerifyOTPAndEstablishPassword = useCallback(async () => {
    if (migrationOTP.length !== 6) {
      showGeneralDialog("Invalid Code", "Please enter a 6-digit code.");
      return;
    }

    if (!migrationPassword || migrationPassword.length < 6) {
      showGeneralDialog("Weak Password", "Password must be at least 6 characters.");
      return;
    }

    if (migrationPassword !== migrationPasswordConfirm) {
      showGeneralDialog("Password Mismatch", "Passwords do not match.");
      return;
    }

    setShowProgressIndicator(true);

    try {
      // Establish password with backend
      await API.post('/user/establishPassword', {
        email,
        otp: migrationOTP,
        password: migrationPassword,
        ...buildMarketingContext('camel'),
      });

      // Sign in with new Firebase password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        migrationPassword
      );

      await doFirebaseLogin(userCredential.user, undefined, 'email_migration');
    } catch (err) {
      setShowProgressIndicator(false);
      if (err.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(err, email);
      } else {
        handleApiError(err);
      }
    }
  }, [buildMarketingContext, email, migrationOTP, migrationPassword, migrationPasswordConfirm, doFirebaseLogin, handleApiError, handleMFARequired, showGeneralDialog]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) return;
    setForgotPasswordLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setForgotPasswordSent(true);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      showGeneralDialog("Error", "Failed to send password reset email. Please check the email address and try again.");
    } finally {
      setForgotPasswordLoading(false);
    }
  }, [email, showGeneralDialog]);

  const handleCloseMigrationDialog = useCallback(() => {
    setShowMigrationDialog(false);
    setEmail('');
    setNeedsPasswordEstablishment(false);
  }, []);

  // Redirect if logged in
  if (redirectToReferrer) {
    return <Redirect to="/" />;
  }

  // Render
  return (
    <div className={classes.root}>
      <Container maxWidth="xs" className={classes.container}>
        <Paper elevation={theme.spacing(2)} className={classes.paper}>
          <div style={{ textAlign: "center" }}>
            <img className={classes.logo} src="/nucleusOneDark2.png" alt="Nucleus One" />
          </div>

          <Grid container direction="column" spacing={1} className={classes.gridControls}
            ref={buttonGridRef}>
            {/* Google Sign-In */}
            <Grid item style={{marginBottom:2}}>
              <GoogleLogin
                onSuccess={handleGoogleLoginResponse}
                onError={() => {
                  console.log('Login Failed');
                }}
                width={googleButtonWidth}
                type="standard"
                logo_alignment="center"
                size="large"
              />
            </Grid>

            {/* Apple Sign-In */}
            <Grid item>
              <Button
                variant="outlined"
                size="large"
                style={{ backgroundColor: "#fff", height: 38 }}
                className={classes.button}
                fullWidth
                onClick={handleAppleSignIn}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <svg style={{ marginRight: 12 }} aria-hidden="true" width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#000000" d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.07-.5-2.05-.48-3.18 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  Sign in with Apple
                </div>
              </Button>
            </Grid>

            {/* Microsoft & LinkedIn - Same Row */}
            <Grid item>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    size="large"
                    style={{ backgroundColor: "#fff", height: 38 }}
                    className={classes.button}
                    fullWidth
                    onClick={handleMicrosoftSignIn}
                    disabled={microsoftSigningIn}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ marginTop: 4, height: 22 }}>
                        <svg aria-hidden="true" width="22" height="22" viewBox="10 10 28 28">
                          <g>
                            <path fill="#f25022" d="M13 11h9v9h-9z"></path>
                            <path fill="#00a4ef" d="M13 21h9v9h-9z"></path>
                            <path fill="#7fba00" d="M23 11h9v9h-9z"></path>
                            <path fill="#ffb900" d="M23 21h9v9h-9z"></path>
                          </g>
                        </svg>
                      </div>
                      <div style={{ marginLeft: 8 }}>Microsoft</div>
                    </div>
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LinkedIn
                    clientId={process.env.REACT_APP_LINKEDIN_SIGNIN_CLIENTID}
                    redirectUri={`${window.location.origin}/login/linkedin`}
                    onSuccess={handleLinkedInSuccess}
                    onFailure={handleLinkedInFailure}
                    scope="r_emailaddress r_liteprofile"
                  >
                    {({ linkedInLogin }) => (
                      <Button
                        variant="outlined"
                        size="large"
                        style={{ backgroundColor: "#fff", height: 38 }}
                        className={classes.button}
                        fullWidth
                        onClick={linkedInLogin}
                      >
                        <img style={{ width: 16, marginRight: 12 }} src="/linkedin.png" alt="Sign in with LinkedIn" />
                        LinkedIn
                      </Button>
                    )}
                  </LinkedIn>
                </Grid>
              </Grid>
            </Grid>

            {/* Or Divider */}
            <Grid item className={classes.or}>
              <Grid container spacing={0} style={{ alignItems: "center" }}>
                <Grid item xs={5}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item xs={2}>
                  or
                </Grid>
                <Grid item xs={5}>
                  <Divider className={classes.divider} />
                </Grid>
              </Grid>
            </Grid>

            {/* Email Input + Submit Button */}
            <Grid item>
              <Grid container spacing={2} style={{alignItems:"center"}}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    size="small"
                    fullWidth
                    label="E-mail Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleEmailPasswordSubmit();
                      }
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    color="secondary"
                    variant="contained"
                    style={{height:40}}
                    className={classes.button}
                    fullWidth
                    onClick={handleEmailPasswordSubmit}
                    disabled={!email || !ValidateEmail(email)}
                  >
                    <span style={{whiteSpace:"nowrap"}}>{isLogin ? 'SIGN IN' : 'SIGN UP'}</span>
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Toggle Login/Signup */}
            <Grid item>
              <Typography variant="body2" align="center">
                {isLogin ? (
                  <>
                    Don't have an account or password?
                    <br />
                    <Link
                      component="button"
                      variant="body2"
                      style={{ color: theme.palette.secondary.main }}
                      onClick={() => setIsLogin(false)}
                    >
                      Establish one now
                    </Link>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <Link
                      component="button"
                      variant="body2"
                      style={{ color: theme.palette.secondary.main }}
                      onClick={() => setIsLogin(true)}
                    >
                      Sign in
                    </Link>
                  </>
                )}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Footer Links */}
        <Grid container spacing={2} className={classes.gridLinks}>
          <Grid item>
            <Link href="/about" color="inherit">
              About
            </Link>
          </Grid>
          <Grid item>
            <Link href="/tos" color="inherit">
              Terms
            </Link>
          </Grid>
          <Grid item>
            <Link href="/privacy" color="inherit">
              Privacy
            </Link>
          </Grid>
        </Grid>
      </Container>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => { setShowPasswordDialog(false); setPassword(''); setForgotPasswordSent(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>{isLogin ? 'Enter Password' : 'Create Password'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordAuth();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputRef={passwordInputRef}
            autoFocus
          />
          {isLogin && (
            <div style={{ marginTop: 12 }}>
              {forgotPasswordSent ? (
                <Typography variant="body2" color="primary">
                  Password reset email sent to {email}. Please check your inbox.
                </Typography>
              ) : (
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleForgotPassword}
                  disabled={forgotPasswordLoading}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Forgot password?'}
                </Link>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPasswordDialog(false); setPassword(''); setForgotPasswordSent(false); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handlePasswordAuth}>
            {isLogin ? 'Sign In' : 'Sign Up'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationDialog} onClose={handleCloseMigrationDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Establish Password for Enhanced Security</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We've upgraded to password-based authentication. To continue:
          </Typography>
          <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
            <li>We'll send a verification code to {maskEmail(email)}</li>
            <li>Enter the code to verify it's you</li>
            <li>Create a secure password</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            After this one-time setup, you'll sign in with your email and password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMigrationDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleStartMigration}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP and Password Establishment Dialog */}
      <Dialog open={showOTPDialog} onClose={() => setShowOTPDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Email and Create Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the 6-digit code sent to {maskEmail(email)}
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={migrationOTP}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setMigrationOTP(val);
            }}
            placeholder="000000"
            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={migrationPassword}
            onChange={(e) => setMigrationPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={migrationPasswordConfirm}
            onChange={(e) => setMigrationPasswordConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOTPDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleVerifyOTPAndEstablishPassword}
            disabled={migrationOTP.length !== 6 || !migrationPassword || migrationPassword !== migrationPasswordConfirm}
          >
            Verify & Create Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Challenge Dialog (Firebase resolver-based) */}
      <MFAChallengeDialog
        open={showMFADialog}
        resolver={mfaResolver}
        email={mfaEmail}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />

      {/* Server-side TOTP Verification Dialog (for LinkedIn/custom token sign-ins) */}
      <Dialog open={showTOTPDialog} onClose={handleTOTPCancel} maxWidth="xs" fullWidth>
        {totpRecoverySuccess ? (
          <>
            <DialogTitle>MFA Reset Complete</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="primary" style={{ marginBottom: 16 }}>
                Multi-factor authentication has been reset successfully.
              </Typography>
              <Typography variant="body2">
                Please sign in again. You will no longer be prompted for an authenticator code.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" color="primary" onClick={handleTOTPCancel}>
                OK
              </Button>
            </DialogActions>
          </>
        ) : totpShowRecovery ? (
          <>
            <DialogTitle>Use Recovery Code</DialogTitle>
            <DialogContent>
              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter one of the recovery codes you saved when you set up MFA.
              </Typography>

              <TextField
                fullWidth
                label="Recovery code"
                value={totpRecoveryCode}
                onChange={(e) => setTotpRecoveryCode(e.target.value)}
                placeholder="xxxx-xxxx"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpRecoveryCode.trim()) {
                    handleTOTPRecoveryVerify();
                  }
                }}
                style={{ marginBottom: 8 }}
              />

              <Typography variant="body2" color="textSecondary">
                After resetting, you can sign in without an authenticator code and optionally re-enroll.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setTotpShowRecovery(false); setTotpRecoveryCode(''); setTotpError(''); }} disabled={totpRecoveryLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTOTPRecoveryVerify}
                disabled={totpRecoveryLoading || !totpRecoveryCode.trim()}
              >
                {totpRecoveryLoading ? 'Resetting...' : 'Reset MFA'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Multi-Factor Authentication</DialogTitle>
            <DialogContent>
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter the 6-digit code from your authenticator app.
              </Typography>

              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <TextField
                fullWidth
                value={totpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTotpCode(val);
                }}
                placeholder="000000"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpCode.length === 6) {
                    handleTOTPVerify();
                  }
                }}
                style={{ marginBottom: 16 }}
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => { setTotpShowRecovery(true); setTotpError(''); }}
                style={{ display: 'block', textAlign: 'center' }}
              >
                Can't access your authenticator?
              </Link>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTOTPCancel} disabled={totpLoading}>Cancel</Button>
              <Button
                onClick={handleTOTPVerify}
                variant="contained"
                color="primary"
                disabled={totpLoading || totpCode.length !== 6}
              >
                {totpLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* General Dialog */}
      <MultiUseDialog Details={generalDialogDetails} />

      {/* Progress Indicator */}
      {showProgressIndicator && <ProgressIndicator />}
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(Login);
